/**
 * Shared hook and dropdown component for autocomplete suggestions in tag-style cell editors.
 * Handles suggestion state, client-side filtering, keyboard navigation, and dropdown rendering.
 */
import { useState, useEffect, useRef, KeyboardEvent } from "react"

import { SuggestionsContainer, SuggestionItem } from "./tag-editor-styles"

/** Configuration for the useSuggestions hook. */
type tUseSuggestionsConfig = {
    /** All available suggestion values to filter from */
    allSuggestions: string[]
    /** Current text input value for filtering */
    inputValue: string
    /** Values already selected (excluded from suggestions) */
    excludeValues: string[]
    /** Callback when a suggestion is selected */
    onSelect: (value: string) => void
    /** Maximum number of suggestions to show (default 5) */
    maxSuggestions?: number
    /** When true, show all suggestions even when input is empty (default false) */
    showAllOnEmpty?: boolean
}

/** Return type for the useSuggestions hook. */
type tUseSuggestionsResult = {
    /** Filtered suggestions matching the current input */
    suggestions: string[]
    /** Whether the suggestions dropdown is visible */
    showSuggestions: boolean
    /** Index of the currently highlighted suggestion (-1 for none) */
    selectedSuggestionIndex: number
    /** Setter for selectedSuggestionIndex */
    setSelectedSuggestionIndex: React.Dispatch<React.SetStateAction<number>>
    /** Ref for the suggestions dropdown container (needed for blur handling) */
    suggestionsRef: React.RefObject<HTMLDivElement>
    /** Select a suggestion by value */
    selectSuggestion: (value: string) => void
    /** Setter for showSuggestions */
    setShowSuggestions: React.Dispatch<React.SetStateAction<boolean>>
    /**
     * Handle keyboard events for suggestion navigation.
     * Returns true if the event was handled (caller should return early).
     */
    handleSuggestionKeyDown: (e: KeyboardEvent<HTMLInputElement>) => boolean
    /**
     * Capture handler to prevent AG-Grid from closing the editor
     * when Enter is pressed to select a suggestion.
     */
    handleSuggestionKeyDownCapture: (e: React.KeyboardEvent<HTMLInputElement>) => void
    /** Returns true if the blur target is within the suggestions dropdown. */
    isSuggestionBlur: (e: React.FocusEvent) => boolean
    /** Re-shows suggestions on input focus if applicable. */
    handleSuggestionFocus: () => void
}

/**
 * Hook for managing autocomplete suggestion state and behavior in tag-style cell editors.
 * Handles filtering, keyboard navigation (ArrowUp/Down, Enter, Escape), and selection.
 *
 * Usage:
 * ```tsx
 * const suggestionState = useSuggestions({
 *     allSuggestions,
 *     inputValue,
 *     excludeValues: selectedValues,
 *     onSelect: (value) => { addValue(value); clearInput() },
 * })
 *
 * // In handleKeyDown: if (suggestionState.handleSuggestionKeyDown(e)) return
 * // In JSX: <SuggestionsDropdown {...suggestionState} />
 * ```
 */
export const useSuggestions = (config: tUseSuggestionsConfig): tUseSuggestionsResult => {
    const { allSuggestions, inputValue, excludeValues, onSelect, maxSuggestions = 5, showAllOnEmpty = false } = config

    const [suggestions, setSuggestions] = useState<string[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
    const suggestionsRef = useRef<HTMLDivElement>(null)
    const currentSuggestionsRef = useRef<string[]>([])
    const currentSelectedIndexRef = useRef<number>(-1)

    // Keep refs in sync with state for use in capture/async event handlers
    useEffect(() => {
        currentSuggestionsRef.current = suggestions
    }, [suggestions])

    useEffect(() => {
        currentSelectedIndexRef.current = selectedSuggestionIndex
    }, [selectedSuggestionIndex])

    // Filter suggestions client-side as input changes
    useEffect(() => {
        if (!allSuggestions.length || (inputValue.length === 0 && !showAllOnEmpty)) {
            setSuggestions([])
            setShowSuggestions(false)
            return
        }

        const filtered = allSuggestions.filter(s => s.toLowerCase().includes(inputValue.toLowerCase()) && !excludeValues.includes(s)).slice(0, maxSuggestions)

        setSuggestions(filtered)
        setShowSuggestions(filtered.length > 0)
        setSelectedSuggestionIndex(-1)
    }, [inputValue, excludeValues, allSuggestions, maxSuggestions, showAllOnEmpty])

    const selectSuggestion = (value: string) => {
        onSelect(value)
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
    }

    const handleSuggestionKeyDown = (e: KeyboardEvent<HTMLInputElement>): boolean => {
        if (!showSuggestions || suggestions.length === 0) return false

        if (e.key === "ArrowDown") {
            e.preventDefault()
            setSelectedSuggestionIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0))
            return true
        } else if (e.key === "ArrowUp") {
            e.preventDefault()
            setSelectedSuggestionIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1))
            return true
        } else if (e.key === "Enter") {
            const currentIndex = currentSelectedIndexRef.current
            const currentSuggestions = currentSuggestionsRef.current
            if (currentIndex >= 0 && currentIndex < currentSuggestions.length) {
                e.preventDefault()
                e.stopPropagation()
                selectSuggestion(currentSuggestions[currentIndex])
                return true
            }
            // Suggestions visible but none selected — still prevent AG-Grid
            // from closing the editor, but return false so the caller can
            // handle adding the typed value
            e.stopPropagation()
            return false
        } else if (e.key === "Escape") {
            e.preventDefault()
            setShowSuggestions(false)
            setSelectedSuggestionIndex(-1)
            return true
        }

        return false
    }

    const handleSuggestionKeyDownCapture = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && showSuggestions && selectedSuggestionIndex >= 0) {
            e.preventDefault()
            e.stopPropagation()
            const currentSuggestions = currentSuggestionsRef.current
            if (selectedSuggestionIndex < currentSuggestions.length) {
                selectSuggestion(currentSuggestions[selectedSuggestionIndex])
            }
        }
    }

    const isSuggestionBlur = (e: React.FocusEvent): boolean => {
        return !!suggestionsRef.current?.contains(e.relatedTarget as Node)
    }

    const handleSuggestionFocus = () => {
        if (suggestions.length > 0 && (inputValue.length > 0 || showAllOnEmpty)) {
            setShowSuggestions(true)
        }
    }

    return {
        suggestions,
        showSuggestions,
        selectedSuggestionIndex,
        setSelectedSuggestionIndex,
        suggestionsRef,
        selectSuggestion,
        setShowSuggestions,
        handleSuggestionKeyDown,
        handleSuggestionKeyDownCapture,
        isSuggestionBlur,
        handleSuggestionFocus,
    }
}

/** Props for the SuggestionsDropdown component (subset of useSuggestions return). */
type tSuggestionsDropdownProps = Pick<tUseSuggestionsResult, "suggestions" | "showSuggestions" | "selectedSuggestionIndex" | "suggestionsRef" | "selectSuggestion" | "setSelectedSuggestionIndex">

/**
 * Dropdown component for displaying autocomplete suggestions.
 * Renders the suggestion list with keyboard and mouse interaction support.
 */
export const SuggestionsDropdown = ({
    suggestions,
    showSuggestions,
    selectedSuggestionIndex,
    suggestionsRef,
    selectSuggestion,
    setSelectedSuggestionIndex,
}: tSuggestionsDropdownProps): JSX.Element | null => {
    if (!showSuggestions || suggestions.length === 0) return null

    return (
        <SuggestionsContainer ref={suggestionsRef}>
            {suggestions.map((suggestion, index) => (
                <SuggestionItem
                    key={suggestion}
                    isSelected={index === selectedSuggestionIndex}
                    onMouseDown={(e: React.MouseEvent) => {
                        e.preventDefault()
                        selectSuggestion(suggestion)
                    }}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                >
                    {suggestion}
                </SuggestionItem>
            ))}
        </SuggestionsContainer>
    )
}
